'use client'

import { useState, useTransition } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@ui/primitives/Button'
import { Icon } from '@ui/primitives/Icon'
import { trackEvent } from '@/lib/analytics/client'

interface LikeButtonProps {
  tablatureId: number
  initialLikesCount: number
  initialUserLiked: boolean
  isAuthenticated: boolean
  dictionary: any
}

export function LikeButton({
  tablatureId,
  initialLikesCount,
  initialUserLiked,
  isAuthenticated,
  dictionary,
}: LikeButtonProps) {
  const [likesCount, setLikesCount] = useState(initialLikesCount)
  const [userLiked, setUserLiked] = useState(initialUserLiked)
  const [isPending, startTransition] = useTransition()
  const router = useRouter()

  const handleLike = async () => {
    if (!isAuthenticated) {
      router.push('/login?redirect=' + encodeURIComponent(window.location.pathname))
      return
    }

    const previousLiked = userLiked
    const previousCount = likesCount

    // Optimistic update
    setUserLiked(!userLiked)
    setLikesCount(prev => userLiked ? prev - 1 : prev + 1)

    startTransition(async () => {
      try {
        const method = previousLiked ? 'DELETE' : 'POST'
        const response = await fetch(`/api/v1/tablatures/${tablatureId}/like`, {
          method,
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        })

        if (!response.ok) {
          throw new Error('Failed to like/unlike')
        }

        // Track analytics
        trackEvent('tablature_like', {
          id: String(tablatureId),
        })
      } catch (error) {
        // Revert optimistic update
        setUserLiked(previousLiked)
        setLikesCount(previousCount)
        console.error('Failed to like/unlike:', error)
      }
    })
  }

  return (
    <Button
      onClick={handleLike}
      disabled={isPending}
      variant="ghost"
      size="md"
      data-testid="tablature-like-button"
      className={`transition-colors duration-200 ${userLiked ? 'text-red-500 hover:text-red-600 hover:bg-red-50' : 'text-gray-500 hover:text-red-500 hover:bg-red-50'}`}
      title={userLiked ? dictionary?.actions?.unlike || 'Unlike' : dictionary?.actions?.like || 'Like'}
    >
      <i className={`fa-solid fa-heart mr-2 ${userLiked ? 'text-red-500' : 'text-gray-400 group-hover:text-red-500'}`} />
      {likesCount > 0 && <span className="font-medium">{likesCount}</span>}
      <span className="hidden sm:inline ml-1">
        {userLiked
          ? (dictionary?.actions?.unlike || 'Unlike')
          : (dictionary?.actions?.like || 'Like')
        }
      </span>
    </Button>
  )
}
